import type {ComponentType} from 'react';
import type {IconProps} from '../../assets/icons/types';
import {STATIC_PATH} from '../../config/config';
import AiAgentCalendarIcon from '../../assets/icons/AiAgentCalendarIcon';
import AiAgentPencilIcon from '../../assets/icons/AiAgentPencilIcon';
import AiAgentTicketsIcon from '../../assets/icons/AiAgentTicketsIcon';
import AiAgentAttachmentIcon from '../../assets/icons/AiAgentAttachmentIcon';

const VIDEO_PATH = `${STATIC_PATH}images/app/aiAgent`;

/** The FAB and the empty state share one video, and with it one poster frame. */
const ASSISTANT_VIDEO_SRC = `${VIDEO_PATH}/assistant-fab-star.mp4`;
// The poster is the reduced-motion fallback, so it has to exist: assistant-fab-star-poster.jpg
// was never uploaded and returns 403. Point a dedicated FAB frame here once one is on S3.
const ASSISTANT_POSTER_SRC = `${VIDEO_PATH}/assistant-fab-poster.jpg`;

export const AI_AGENT_MEDIA = {
    fab: {
        src: ASSISTANT_VIDEO_SRC,
        poster: ASSISTANT_POSTER_SRC,
    },
    empty: {
        src: ASSISTANT_VIDEO_SRC,
        poster: ASSISTANT_POSTER_SRC,
    },
} as const;

export type SuggestionIcon = ComponentType<IconProps>;

/**
 * Suggestions themselves come from GET aiAgentMeta; icons and explanations are client
 * presentation, keyed by intent — an intent the maps do not know falls back to the
 * default icon and no hint.
 */
export const AI_AGENT_SUGGESTION_ICONS: Record<string, SuggestionIcon> = {
    CREATE_EVENT: AiAgentCalendarIcon,
    CREATE_TICKET: AiAgentTicketsIcon,
};

export const AI_AGENT_SUGGESTION_DEFAULT_ICON: SuggestionIcon = AiAgentPencilIcon;

/** What the agent will ask for once the prompt runs, so the click is not a leap of faith. */
export const AI_AGENT_SUGGESTION_DESCRIPTION_KEYS: Record<string, string> = {
    CREATE_EVENT: 'suggestionCreateEventDescription',
    CREATE_TICKET: 'suggestionCreateTicketDescription',
};

/** What the chat says once the record it agreed on exists — named after what was created. */
export const AI_AGENT_CONFIRMED_TEXT_KEYS: Record<string, string> = {
    CREATE_EVENT: 'proposalConfirmedCreateEvent',
    CREATE_TICKET: 'proposalConfirmedCreateTicket',
};

export const AI_AGENT_CONFIRMED_TEXT_DEFAULT_KEY = 'proposalConfirmed';

/** The "go to page" button under the chat once its record exists — named after what it opens. */
export const AI_AGENT_GO_TO_PAGE_KEYS: Record<string, string> = {
    CREATE_EVENT: 'goToPageCreateEvent',
    CREATE_TICKET: 'goToPageCreateTicket',
};

export const AI_AGENT_GO_TO_PAGE_DEFAULT_KEY = 'goToPage';

/**
 * Client-authored variants of a server intent (the attach flow is client code). Each is
 * shown right after its intent's own chip, only when the server offers a suggestion for
 * that intent in this scope.
 */
export interface AiAgentExtraSuggestion {
    intent: string;
    labelKey: string;
    descriptionKey: string;
    promptKey: string;
    Icon: SuggestionIcon;
    /** Opens the same file picker as the prompt attach button. */
    opensFilePicker?: boolean;
}

export const AI_AGENT_EXTRA_SUGGESTIONS: AiAgentExtraSuggestion[] = [
    {
        intent: 'CREATE_EVENT',
        labelKey: 'suggestionCreateEventFromImage',
        descriptionKey: 'suggestionCreateEventFromImageDescription',
        promptKey: 'promptCreateEventFromImage',
        Icon: AiAgentAttachmentIcon,
        opensFilePicker: true,
    },
];
